import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";

import type { LoginResponse } from "@/features/auth/types";
import { getAccessToken, getRefreshToken, setSession } from "@/lib/auth-storage";
import { env } from "@/lib/config";

/**
 * axios's default array serialization emits `key[]=a&key[]=b`. Fastify's querystring parser
 * (fast-querystring) only turns a repeated *bare* key into an array — `key[]` comes through as
 * a literal `"key[]"` field name, not `key` with an array value. Multi-select filters (e.g.
 * ProductsQueryDto's warehouseIds/categoryIds/stickerIds) rely on the bare-repeated-key form, so
 * every array param needs to serialize that way.
 */
function serializeParams(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null) search.append(key, String(item));
      }
    } else {
      search.append(key, String(value));
    }
  }
  return search.toString();
}

export const api = axios.create({
  baseURL: env.apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
  paramsSerializer: { serialize: serializeParams },
});

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  /** Set once a request has already gone through one refresh-and-retry cycle, so a request that
   * 401s again right after (vd the refreshed access token itself getting rejected somehow)
   * fails outright instead of looping refresh attempts forever. */
  _retriedAfterRefresh?: boolean;
}

/** Auth endpoints that must never trigger (or be retried through) the refresh flow — refresh
 * itself failing IS the "session is truly over" signal, not something to refresh-and-retry. */
const AUTH_ENDPOINTS = ["/auth/login", "/auth/refresh", "/auth/logout"];

function isAuthEndpoint(url: string | undefined): boolean {
  return Boolean(url && AUTH_ENDPOINTS.some((path) => url.includes(path)));
}

function logout(): void {
  setSession(null);
  window.dispatchEvent(new CustomEvent("auth:logout"));
}

/** At most 1 refresh in flight at a time — several requests 401ing together (vd a page firing
 * off a handful of queries at once right as the access token expires) share the same refresh
 * instead of each spending their own refresh token and racing to rotate it out from under one
 * another (refresh tokens are rotated on every use, see AuthService.refresh). */
let refreshPromise: Promise<void> | null = null;

async function refreshSession(): Promise<void> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error("No refresh token to refresh with");
  }
  const response = await api.post<LoginResponse>("/auth/refresh", { refreshToken });
  setSession(response.data);
}

/**
 * Every 2xx response from the API is wrapped as
 * `{ success, data, timestamp, path }` (see handle_api.txt §1.1). Unwrap it
 * here so the rest of the app can keep reading `response.data` directly as
 * the actual payload.
 */
api.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      typeof response.data === "object" &&
      "data" in response.data &&
      "success" in response.data
    ) {
      response.data = response.data.data;
    }
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as RetryableRequestConfig | undefined;
    const canRetryWithRefresh =
      error.response?.status === 401 &&
      config &&
      !config._retriedAfterRefresh &&
      !isAuthEndpoint(config.url) &&
      Boolean(getRefreshToken());

    if (canRetryWithRefresh) {
      config._retriedAfterRefresh = true;
      try {
        refreshPromise ??= refreshSession().finally(() => {
          refreshPromise = null;
        });
        await refreshPromise;
        return api(config);
      } catch {
        logout();
        return Promise.reject(error);
      }
    }

    // Either not a 401, already retried once, an auth endpoint itself, or there was no refresh
    // token to try in the first place — in all of those cases the session really is over.
    if (error.response?.status === 401) {
      logout();
    }
    return Promise.reject(error);
  },
);
